import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_TIMEOUT_MS = 4000;

/**
 * Interactions that count as "user is here" — any of these reveals the
 * controls and restarts the idle timer. `focusin` covers keyboard (Tab)
 * navigation; `scroll`/`wheel` cover trackpad/touch scrolling.
 *
 * `pointerdown` is intentionally excluded: including it would immediately
 * undo a manual tap-to-hide (the tap fires pointerdown → auto-reveal before
 * the hide callback even completes). Tap-to-reveal is handled by the
 * ToggleControlsArea click handler; `pointermove` and `touchstart` still
 * reveal on genuine swipe/hover interactions.
 *
 * `click` and `pointerup` are also intentionally absent: a stationary click
 * (programmatic or a11y-activated, with no prior pointermove) will NOT
 * reveal controls on its own — `keydown` covers keyboard activation and
 * `touchstart` covers touch. Narrow gap, only relevant to a future
 * video tap-toggle.
 */
const INTERACTION_EVENTS = [
    'pointermove',
    'keydown',
    'focusin',
    'wheel',
    'scroll',
    'touchstart',
] as const;

// Hiding unmounts the controls, so hiding under a keyboard-focused control drops focus to the dialog root.
const hasKeyboardFocusWithin = (scope: Element) => {
    const active = document.activeElement;
    return (
        active instanceof HTMLElement &&
        active.tabIndex !== -1 &&
        scope.contains(active) &&
        active.matches(':focus-visible')
    );
};

export interface UseAutoHideControlsOptions {
    /** Arm the idle timer only during active playback. */
    isPlaying: boolean;
    /** When true (e.g. a menu is open), force-show controls and never arm. */
    isSuppressed?: boolean;
    /** Idle duration before hiding, in ms. Defaults to 4000. */
    timeoutMs?: number;
    /**
     * Scope interaction listeners to this element. When omitted, listens on
     * `window` (suitable for full-screen dialogs).
     */
    targetRef?: RefObject<Element | null>;
}

export interface UseAutoHideControlsResult {
    areControlsHidden: boolean;
    /** Show controls and restart the idle timer (for manual toggles). */
    reset: () => void;
    /** Immediately hide controls and cancel the idle timer. */
    hide: () => void;
}

export function useAutoHideControls({
    isPlaying,
    isSuppressed = false,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    targetRef,
}: UseAutoHideControlsOptions): UseAutoHideControlsResult {
    const [areControlsHidden, setAreControlsHidden] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );

    const shouldAutoHide = isPlaying && !isSuppressed;

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
        }
    }, []);

    const reset = useCallback(() => {
        clearTimer();
        setAreControlsHidden(false);
        if (!shouldAutoHide) return;

        const armTimer = () => {
            timerRef.current = setTimeout(() => {
                if (
                    hasKeyboardFocusWithin(targetRef?.current ?? document.body)
                ) {
                    armTimer();
                    return;
                }
                setAreControlsHidden(true);
            }, timeoutMs);
        };
        armTimer();
    }, [clearTimer, shouldAutoHide, timeoutMs, targetRef]);

    useEffect(() => {
        // Paused or suppressed: always show controls, never arm the timer.
        if (!shouldAutoHide) {
            clearTimer();
            setAreControlsHidden(false);
            return;
        }

        // Arm the initial idle timer.
        reset();

        const target: EventTarget = targetRef?.current ?? window;
        const resetOnUserActivity = (event: Event) => {
            // A dialog focus trap refocuses its tabindex=-1 root once a focused
            // control unmounts while hiding. Treating that as activity reveals
            // the controls straight after they hide.
            if (
                event.type === 'focusin' &&
                event.target instanceof HTMLElement &&
                event.target.tabIndex === -1
            ) {
                return;
            }
            reset();
        };

        INTERACTION_EVENTS.forEach((eventName) =>
            target.addEventListener(eventName, resetOnUserActivity, {
                passive: true,
            })
        );

        return () => {
            INTERACTION_EVENTS.forEach((eventName) =>
                target.removeEventListener(eventName, resetOnUserActivity)
            );
            clearTimer();
        };
    }, [shouldAutoHide, reset, clearTimer, targetRef]);

    const hide = useCallback(() => {
        // Honor the paused/suppressed contract: never hide while paused or
        // suppressed (a consumer's manual hide is ignored in that state).
        if (!shouldAutoHide) return;
        clearTimer();
        setAreControlsHidden(true);
    }, [shouldAutoHide, clearTimer]);

    return { areControlsHidden, reset, hide };
}
